import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { ReportIssueProjectId, ReportIssueSenderType } from 'webapp/enums/enums';

import FormTextarea from 'webapp/components/form/textarea';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButton from 'webapp/components/submit-button';

export default class CommonPopupsPopupReportIssueComponent extends PopupInnerModalComponent<PopupData.Common.ReportIssue> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('issues-report') declare issuesReportService: Service.IssuesReport;

	@tracked override submitInProgress = false;
	userComment = '';

	@action
	sendFeedback(): void {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;

		const { id: accountId, company } = this.accountService;
		const { id: currentUserId, email } = this.currentUserService.model!;
		const { endpoint, httpStatusCode, requestId, isTimeout, senderType } = this.popupData;

		let sentryApiUrl = 'https://databox.sentry.io/issues/';

		if (senderType && senderType === ReportIssueSenderType.DATASET_EVENTS) {
			sentryApiUrl += `?project=${ReportIssueProjectId.DATASET_EVENTS}&query=request-id%3A${requestId}`;
		} else {
			sentryApiUrl += `?project=${ReportIssueProjectId.ANALYTICS_API}&query=requestId%3A${requestId}`;
		}

		let diagnosticInfo = `*Account:* [${accountId}] ${company}`;

		if (this.accountService.isAgency) {
			diagnosticInfo += ' (organization)';
		} else if (this.accountService.isClient) {
			diagnosticInfo += ' (account)';
		}

		diagnosticInfo += `\\n*User:* [${currentUserId}] ${email}`;
		diagnosticInfo += `\\n*PALoginLink:* <${this.accountService.getPALoginLink()}|[link]>`;
		diagnosticInfo += `\\n*Endpoint:* ${endpoint}`;
		diagnosticInfo += `\\n*HTTP Status Code:* ${httpStatusCode}`;

		if (isTimeout) {
			diagnosticInfo += ` (Timeout)`;
		}

		diagnosticInfo += `\\n*Request ID:* ${requestId}`;

		if (!isTimeout) {
			diagnosticInfo += `\\n*Sentry:* <${sentryApiUrl}|[link]>`;
		}

		const data: { diagnosticInfo: string; userComment?: string } = {
			diagnosticInfo,
		};

		if (this.userComment) {
			data.userComment = JSON.stringify(this.userComment);
		}

		this.requestService
			.api('POST', '/feedbacks/report-issue', data)
			.then(() => {
				this.issuesReportService.triggerEventIssueReported(this.popupData.eventIssueData);
				this.infolayerService.ok('Your report was successfully sent!');
			})
			.finally(() => {
				this.submitInProgress = false;
				this.close();
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	<template>
		<div>
			<div class="title">Report technical issue</div>
			<div class="content">
				<FormTextarea
					name="comment"
					disabled={{this.submitInProgress}}
					placeholder="Provide any steps necessary to reproduce the problem."
					maxlength="1000"
					@value={{this.userComment}}
					@hideFloatLabel={{true}}
				/>
				<div class="controls2">
					<div class="controls2__right">
						<SubmitButton
							@text="Cancel"
							@theme="grey-empty"
							@noDelay={{true}}
							@onTrigger={{this.close}}
						/>
						<SubmitButton
							@text="Submit report"
							@onTrigger={{this.sendFeedback}}
							@submitInProgress={{this.submitInProgress}}
						/>
					</div>
				</div>
			</div>
		</div>
	</template>
}
